import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ChatHistory = () => {
  const { 
    chats, 
    loadChats, 
    createChat,
    deleteChat,
    updateChatTitle,
    loadChat,
    loading 
  } = useChat();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [newChatTitle, setNewChatTitle] = useState('');
  const navigate = useNavigate();
  
  // Get authentication state
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Load chats when authenticated
  useEffect(() => {
    // Only load chats when authenticated and auth loading is complete
    if (isAuthenticated && !authLoading) {
      console.log('Authentication confirmed in history, loading chats...');
      
      // Add a small delay to ensure auth headers are set
      const timeout = setTimeout(() => {
        loadChats();
      }, 300);
      
      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, authLoading, loadChats]);
  
  // Format date string
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };
  
  // Handle opening chat
  const handleOpenChat = (chat) => {
    loadChat(chat.id);
    navigate('/chat');
  };
  
  // Handle edit dialog
  const handleEditChat = (chat) => {
    setSelectedChat(chat);
    setEditedTitle(chat.title);
    setEditDialogOpen(true);
  };
  
  // Handle delete dialog
  const handleDeleteChat = (chat) => {
    setSelectedChat(chat);
    setDeleteDialogOpen(true);
  };
  
  // Save edited title
  const handleSaveEdit = () => {
    if (selectedChat && editedTitle.trim()) {
      updateChatTitle(selectedChat.id, editedTitle);
      setEditDialogOpen(false);
    }
  };
  
  // Confirm delete
  const handleConfirmDelete = () => {
    if (selectedChat) {
      deleteChat(selectedChat.id);
      setDeleteDialogOpen(false);
    }
  };
  
  // Create new chat and navigate to chat page
  const handleNewChat = () => {
    const title = newChatTitle.trim() || 'New Chat';
    createChat(title).then(chat => {
      navigate('/chat');
    });
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Chat History
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            placeholder="New chat title (optional)"
            size="small"
            value={newChatTitle}
            onChange={(e) => setNewChatTitle(e.target.value)}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewChat}
            sx={{ 
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333',
              }
            }}
          >
            New Chat
          </Button>
        </Box>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: 'black' }} />
        </Box>
      ) : chats.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ChatIcon sx={{ fontSize: 60, color: '#aaa', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No chat history yet
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Start a new conversation with StateShift Pathfinder
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/chat')}
            sx={{ 
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333',
              }
            }}
          >
            Start a New Chat
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <List sx={{ width: '100%' }}>
            {chats.map((chat, index) => (
              <React.Fragment key={chat.id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  button
                  onClick={() => handleOpenChat(chat)}
                  sx={{ 
                    py: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                  }}
                >
                  <ListItemIcon>
                    <ChatIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={chat.title}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondary={`Created ${formatDate(chat.created_at)}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton 
                      edge="end" 
                      aria-label="edit" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditChat(chat);
                      }}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      edge="end" 
                      aria-label="delete" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteChat(chat);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Chat Title</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Chat Title"
            type="text"
            fullWidth
            variant="outlined"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            sx={{ 
              color: 'black',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this chat? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ChatHistory;
